import React, { useState, useEffect } from 'react';
import { useRef } from 'react';
import axios from 'axios';
import Paper from '@mui/material/Paper';
import SearchIcon from '@mui/icons-material/Search';
import * as XLSX from 'xlsx';
import { fetchData, updateData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { IconButton } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import FeedIcon from '@mui/icons-material/Feed';
import Select from 'react-select'; // Import react-select
import CopyrightIcon from '@mui/icons-material/Copyright';
import CircularProgress from '@mui/material/CircularProgress';
import { Box } from '@mui/material';
import CreateIcon from '@mui/icons-material/Create';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { Tooltip } from '@mui/material';
import {
    Checkbox, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TableSortLabel, TablePagination, TableFooter
} from '@mui/material';
import Modal from '@mui/material/Modal';
import { Button } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ActivateIcon from '@mui/icons-material/CheckCircle';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { useLocation, Link } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import _ from "lodash";

function StockCategoryWise() {
    const { isAuthenticated, companyId, companyName, branchId, permissions, token, logout } = useAuth();

    const [data, setData] = useState([]);
    const [fromDate, setFromDate] = useState(getFormattedStartDate());
    const [toDate, setToDate] = useState(getFormattedCurrentDate());
    const [loading, setLoading] = useState(false);

    const [StyleOptions, setStyleOptions] = useState([]);
    const [selectedStyle, setSelectedStyle] = useState(null);
    const [BrandOptions, setBrandOptions] = useState([]);
    const [selectedBrand, setSelectedBrand] = useState(null);

    const hasReadReportPermission = permissions.includes("Read Reports");

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        fetchTableData();
    }, [isAuthenticated, companyId, branchId, permissions.join(","), token]);

    useEffect(() => {
        fetchStyleData();
        fetchBrandData();
    }, []);

    function getFormattedCurrentDate() {
        const currentDate = new Date();
        return currentDate.toISOString().split("T")[0];
    }

    function getFormattedStartDate() {
        const monthStartDate = new Date();
        monthStartDate.setDate(1);
        return monthStartDate.toISOString().split("T")[0];
    }

    const handleFromDateChange = (e) => setFromDate(e.target.value);
    const handleToDateChange = (e) => setToDate(e.target.value);

    const fetchTableData = async () => {
        if (!toDate) return;
        if (!hasReadReportPermission) return;

        setLoading(true);
        setData([]);

        try {
            //let bid = selectedBranch ? selectedBranch.value : branchId;
            //let categoryid = selectedStyle ? selectedStyle.value : 0;
            let itemId = 0;
            let brandId = selectedBrand ? selectedBrand.value : 0;
            let styleId = selectedStyle ? selectedStyle.value : 0;

            const url = `/StockCategory/${fromDate}/${toDate}/${companyId}/${branchId}/${itemId}/${brandId}/${styleId}`;
            const response = await fetchData("ReportApi", url, token);

            if (response.responseCode === "1111") {
                setData(response.response_data);
                if (selectedStyle.value && selectedBrand.value) {
                    setSelectedBrand('');
                    setSelectedStyle('');
                    setIsAllChecked(false);
                }
                else {
                    setIsAllChecked(true);
                }
            }

        } catch (err) {
            console.error("Error fetching data:", err);
        } finally {
            setLoading(false);
        }
    };

    // ✅ Totals
    const totals = {
        ClosingQty: data.reduce((sum, row) => sum + (row.ClosingQty || 0), 0),
    };

    const grandTotals = { ClosingQty: totals.ClosingQty }; // same here since no grouping in table

    const fetchStyleData = async () => {
        try {
            const response = await fetchData("StyleApi", `/${companyId}`, token);
            const options = response.response_data.map((s) => ({
                label: s.Name,
                value: s.Id,
            }));
            setStyleOptions(options);
            // setIsAllChecked(false);
        } catch (err) {
            console.error("Error fetching styles:", err);
        }
    };

    const fetchBrandData = async () => {
        try {
            const response = await fetchData('BrandApi', `/${companyId}`, token);
            const options = response.response_data.map((b) => ({
                label: b.Name,
                value: b.Id,
            }));
            setBrandOptions(options);
            // setIsAllChecked(false);
        } catch (err) {
            console.error("Error fetching Brand Data:", err);
        }
    };

    const handleBrandSelect = (opt) => setSelectedBrand(opt);
    const handleStyleChange = (opt) => setSelectedStyle(opt);

    const groupedData = _.groupBy(data, "StyleName");

    const [isAllChecked, setIsAllChecked] = useState(true); // ✅ default checked

    useEffect(() => {
        if (isAllChecked) {
            setSelectedBrand(null);
            setSelectedStyle(null);
            fetchTableData();
        }
    }, [isAllChecked]);

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    };


    const handleExportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Stock Report");

        worksheet.views = [{ state: "frozen", xSplit: 0, ySplit: 2 }]; // freeze header

        // Define headers (same as Table)
        const headerRowValues = [
            "Code",
            "Item",
            "HSN Code",
            "GST Rate",
            "Brand",
            "Days",
            "Last Bill Date",
            "Closing Qty",
            "MRP",
            "Selling Price",
        ];

        // Right align numeric fields
        const rightAlignColumns = ["Days", "Closing Qty"];

        // Title
        worksheet.mergeCells("A1:H1");
        const captionCell = worksheet.getCell("A1");
        captionCell.value = `${companyName} - Stock Category Wise Report: ${formatDate(
            fromDate
        )} To: ${formatDate(toDate)}`;
        captionCell.alignment = { vertical: "middle", horizontal: "left" };
        captionCell.font = { bold: true, size: 14 };

        // Header Row
        const headerRow = worksheet.addRow(headerRowValues);
        const thinBorder = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" }
        };

        headerRow.eachCell((cell) => {
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FF27445D" }
            };
            cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
            cell.alignment = {
                vertical: "middle",
                horizontal: "center",
                wrapText: true
            };
            cell.border = thinBorder;
        });

        const lastColumn = 8;
        // Add grouped data
        Object.entries(groupedData).forEach(([style, rows]) => {
            // Group Header Row
            const groupRow = worksheet.addRow([style]);
            groupRow.font = { bold: true, size: 12, color: { argb: "FF08396B" } };
            // groupRow.fill = {
            //     type: "pattern",
            //     pattern: "solid",
            //     fgColor: { argb: "FFDFE7F0" }
            // };

            for (let i = 1; i <= lastColumn; i++) {
                const cell = groupRow.getCell(i);
                cell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "FFDFE7F0" }, // light green
                };
            }



            // Merge across ALL columns (Code → Closing Qty)
            worksheet.mergeCells(
                groupRow.number,
                1,
                groupRow.number,
                headerRowValues.length
            );

            // Child rows
            rows.forEach((row) => {
                const rowValues = [
                    row.Code,
                    row.Name,
                    row.HSNCode,
                    row.GSTRate,
                    row.BrandName,
                    row.DaysSinceLastSale,
                    row.LastSaleDate ? row.LastSaleDate : "-",
                    row.ClosingQty,
                    row.MRP,
                    row.SellingPrice,
                ];

                const excelRow = worksheet.addRow(rowValues);

                excelRow.eachCell((cell, colNumber) => {
                    const headerName = headerRowValues[colNumber - 1];
                    if (rightAlignColumns.includes(headerName)) {
                        cell.alignment = { vertical: "middle", horizontal: "right" };
                        if (typeof cell.value === "number") {
                            cell.numFmt = "#,##0";
                        }
                    } else {
                        cell.alignment = { vertical: "middle", horizontal: "left" };
                    }
                    cell.border = thinBorder;
                });
            });
        });

        // Totals row
        const totalRow = worksheet.addRow([
            "",
            "",
            "",
            "",
            "",
            "Total",
            "",
            totals.ClosingQty
        ]);
        totalRow.font = { bold: true, color: { argb: "FF8B0000" } };
        totalRow.eachCell((cell, colNumber) => {
            if (colNumber === 8) {
                cell.alignment = { vertical: "middle", horizontal: "right" };
            } else {
                cell.alignment = { vertical: "middle", horizontal: "center" };
            }
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFFDEDEC" }
            };
            cell.border = thinBorder;
        });

        // Grand Totals row
        const grandTotalRow = worksheet.addRow([
            "",
            "",
            "",
            "",
            "",
            "Grand Total",
            "",
            grandTotals.ClosingQty
        ]);
        grandTotalRow.font = { bold: true, color: { argb: "FF08396B" } };
        grandTotalRow.eachCell((cell, colNumber) => {
            if (colNumber === 8) {
                cell.alignment = { vertical: "middle", horizontal: "right" };
            } else {
                cell.alignment = { vertical: "middle", horizontal: "center" };
            }
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFDFE7F0" }
            };
            cell.border = thinBorder;
        });

        // Adjust column width
        worksheet.columns = headerRowValues.map(() => ({ width: 18 }));

        // Export
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), "Stock_Category_Wise_Report.xlsx");
    };


    return (
        <div className="w-full mx-auto" style={{ width: "95%", marginTop: "10px" }}>

            <h6
                className="block uppercase tracking-wide text-black productHead flex-grow linkcolor"
                style={{ fontFamily: "Georgia, Cambria, Times New Roman, Times, serif", color: "black" }}
            >
                Stock Category Wise
            </h6>
            {loading && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999
                }}>
                    <div>
                        <CircularProgress style={{ color: 'white' }} />
                        <span style={{ color: 'white', marginTop: '10px', display: 'block' }}>Loading...</span>
                    </div>
                </div>
            )}

            <div className="flex items-center gap-4 w-full" style={{ marginTop: "-10px" }}>


                {/* Brand */}
                <div className="flex items-center gap-2" style={{ marginTop: "20px" }}>
                    <label className="uppercase text-black text-xs whitespace-nowrap">Brand</label>
                    <Select
                        value={selectedBrand}
                        onChange={handleBrandSelect}
                        options={BrandOptions}
                        placeholder="Select"
                        isSearchable
                        menuPortalTarget={document.body}   // 👈 forces dropdown outside container
                        styles={{
                            control: (base) => ({
                                ...base,
                                minHeight: "30px",
                                height: "30px",
                                fontSize: "14px",
                                width: '250px'
                            }),
                            valueContainer: (base) => ({
                                ...base,
                                height: "30px",
                                padding: "0 6px",
                            }),
                            indicatorsContainer: (base) => ({
                                ...base,
                                height: "30px",
                            }),
                            menuPortal: (base) => ({ ...base, zIndex: 2000 }), // 👈 make sure it overlays dialog
                            menu: (provided) => ({
                                ...provided,
                                fontSize: "14px",
                            }),
                        }}
                    />
                </div>

                {/* Style */}
                <div className="flex items-center gap-2" style={{ marginTop: "20px" }}>
                    <label className="uppercase text-black text-xs whitespace-nowrap">Style</label>
                    <Select
                        value={selectedStyle}
                        onChange={handleStyleChange}
                        options={StyleOptions}
                        placeholder="Select"
                        isSearchable
                        menuPortalTarget={document.body}   // 👈 forces dropdown outside container
                        styles={{
                            control: (base) => ({
                                ...base,
                                minHeight: "30px",
                                height: "30px",
                                fontSize: "14px",
                                width: '250px'
                            }),
                            valueContainer: (base) => ({
                                ...base,
                                height: "30px",
                                padding: "0 6px",
                            }),
                            indicatorsContainer: (base) => ({
                                ...base,
                                height: "30px",
                            }),
                            menuPortal: (base) => ({ ...base, zIndex: 2000 }), // 👈 make sure it overlays dialog
                            menu: (provided) => ({
                                ...provided,
                                fontSize: "14px",
                            }),
                        }}
                    />
                </div>

                {/* All Checkbox */}
                <div className="flex items-center gap-2" style={{ marginTop: "20px" }}>
                    <Checkbox
                        checked={isAllChecked}
                        onChange={(e) => setIsAllChecked(e.target.checked)}
                        sx={{ padding: 0 }}
                    />
                    <label className="uppercase text-black text-xs whitespace-nowrap">All</label>
                </div>

                {/* Dates */}
                <div style={{ maxWidth: "150px" }}>
                    <label className="block uppercase tracking-wide text-black text-xs font-bold mb-2" style={{ fontFamily: "monospace" }}>
                        From Date
                    </label>
                    <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline h-8"
                        type="date" value={fromDate} onChange={handleFromDateChange}
                        style={{ fontSize: '12.5px', marginTop: '-10px', shadow: 'appearance-none', border: 'rounded', width: '100%', paddingY: '2', paddingX: '3', color: 'gray-700', leadingTight: true, focus: { color: 'gray-600', shadow: 'outline' } }}
                    />
                </div>

                <div style={{ maxWidth: "150px" }}>
                    <label className="block uppercase tracking-wide text-black text-xs font-bold mb-2" style={{ fontFamily: "monospace" }}>
                        To Date
                    </label>
                    <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline h-8"
                        type="date" value={toDate} onChange={handleToDateChange}
                        style={{ fontSize: '12.5px', marginTop: '-10px', shadow: 'appearance-none', border: 'rounded', width: '100%', paddingY: '2', paddingX: '3', color: 'gray-700', leadingTight: true, focus: { color: 'gray-600', shadow: 'outline' } }}
                    />
                </div>

                {/* Refresh */}
                <Button
                    style={{ color: "black", height: '36px', marginLeft: '10px', marginTop: '10px' }}
                    onClick={fetchTableData}
                    className="linkcolor"
                >
                    <IconButton onClick={fetchTableData}>
                        {/* <RefreshIcon style={{ color: 'brown' }} /> */}
                    </IconButton>
                    <span style={{ color: 'brown', fontWeight: 'bolder', fontSize: '12px' }}>Refresh</span>
                </Button>
                <Button
                    style={{ color: "black", height: '36px', marginLeft: '10px', marginTop: '10px' }}
                    onClick={handleExportExcel}
                    className="linkcolor"
                >
                    <IconButton onClick={handleExportExcel}>
                        {/* <FeedIcon style={{ color: 'darkgreen' }} /> */}
                    </IconButton>
                    <span style={{ color: 'darkgreen', fontWeight: 'bolder', fontSize: '12px' }}>Excel</span>
                </Button>


            </div>

            <div className="watermark" style={{ position: "absolute", bottom: "10px", right: "20px" }}>
                <CopyrightIcon />
                <b style={{ fontFamily: "Script Bold Italic" }}>EFFTABS SOLUTIONS LLP</b>
            </div>

            {/* Table */}
            <div className="w-full mb-5 flex" style={{ maxHeight: "550px", overflowY: "auto", marginTop: "7px" }}>
                <TableContainer component={Paper}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                {["Code", "Item", "HSN Code", "GST Rate", "Brand", "Days", "Last Bill Date", "Closing Qty", "MRP", "Selling Price"].map((head, i) => (
                                    <TableCell
                                        key={i}
                                        sx={{
                                            backgroundColor: "#27445D",
                                            color: "whitesmoke",
                                            fontWeight: "bolder",
                                            textTransform: "uppercase",
                                            fontSize: "11px",
                                            padding: "3px",
                                            textAlign:
                                                head === "Closing Qty" || head === "MRP" || head === "Selling Price"
                                                    ? "right"
                                                    : "left",
                                        }}
                                    >
                                        {head}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Object.entries(groupedData).map(([style, rows]) => (
                                <React.Fragment key={style}>
                                    {/* Group Header Row */}
                                    <TableRow sx={{ backgroundColor: "#dfe7f0" }}>
                                        <TableCell colSpan={11} sx={{ fontWeight: "bold", fontSize: "12px", color: "#08396B", padding: '3px' }}>
                                            {style}
                                        </TableCell>
                                    </TableRow>

                                    {/* Child rows (brands under this style) */}
                                    {rows.map((row, idx) => (
                                        <TableRow
                                            key={idx}
                                            sx={{
                                                backgroundColor:
                                                    row.ClosingQty < 0
                                                        ? "#ffe6e6"
                                                        : row.ClosingQty === 0
                                                            ? "#fff9c4"
                                                            : "inherit",
                                            }}
                                        >
                                            <TableCell sx={{ fontSize: "11.5px", padding: "5px" }}>{row.Code}</TableCell>
                                            <TableCell sx={{ fontSize: "11.5px", padding: "5px" }}>{row.Name}</TableCell>
                                            <TableCell sx={{ fontSize: "11.5px", padding: "5px" }}>{row.HSNCode}</TableCell>
                                            <TableCell sx={{ fontSize: "11.5px", padding: "5px" }}>{row.GSTRate}</TableCell>
                                            <TableCell sx={{ fontSize: "11.5px", padding: "5px" }}>{row.BrandName}</TableCell>
                                            <TableCell sx={{ fontSize: "11.5px", padding: "5px" }}>{row.DaysSinceLastSale}</TableCell>
                                            <TableCell align="right" sx={{ fontSize: "11.5px", padding: "5px" }}>
                                                {row.LastSaleDate ? row.LastSaleDate : "-"}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontSize: "11.5px", padding: "5px", fontWeight: "bold" }}>
                                                {row.ClosingQty}
                                            </TableCell>
                                            {/* <TableCell align="right" sx={{ fontSize: "11.5px", padding: "5px", fontWeight: "bold" }}>
                                                {row.MRP}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontSize: "11.5px", padding: "5px", fontWeight: "bold" }}>
                                                {row.SellingPrice}
                                            </TableCell> */}
                                            <TableCell align="right" sx={{ fontSize: "11.5px", padding: "5px", fontWeight: "bold" }}>
                                                {Number(row.MRP).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </TableCell>

                                            <TableCell align="right" sx={{ fontSize: "11.5px", padding: "5px", fontWeight: "bold" }}>
                                                {Number(row.SellingPrice).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </TableCell>


                                        </TableRow>
                                    ))}
                                </React.Fragment>
                            ))}

                            {/* Totals Row */}
                            <TableRow>
                                <TableCell colSpan={7} align="right">
                                    <strong style={{ color: "darkred" }}>Total</strong>
                                </TableCell>
                                <TableCell align="right" style={{ color: "darkred", fontWeight: "bold" }}>
                                    {totals.ClosingQty}
                                </TableCell>
                            </TableRow>

                            {/* Grand Totals Row */}
                            <TableRow>
                                <TableCell colSpan={7} align="right">
                                    <strong style={{ color: "#08396B" }}>Grand Total</strong>
                                </TableCell>
                                <TableCell align="right" style={{ color: "#08396B", fontWeight: "bold" }}>
                                    {grandTotals.ClosingQty}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
        </div>
    );
}

export default StockCategoryWise;